import type { MyAuthorization, RoleType } from '@isg/shared-types';
import {
  AKSIYON_OKUMA_SAPKALARI,
  CALISAN_OKUMA_SAPKALARI,
  EGITIM_OKUMA_SAPKALARI,
  EKIPMAN_OKUMA_SAPKALARI,
  FORM_OKUMA_SAPKALARI,
  KULLANICI_OKUMA_SAPKALARI,
  ORGANIZASYON_OKUMA_SAPKALARI,
  RISK_OKUMA_SAPKALARI,
  SAGLIK_OKUMA_SAPKALARI,
  KURUL_OKUMA_SAPKALARI,
  ACIL_DURUM_OKUMA_SAPKALARI,
  DOKUMAN_OKUMA_SAPKALARI,
  SAHA_TURU_OKUMA_SAPKALARI,
  SOZLUK_OKUMA_SAPKALARI,
  UYGUNSUZLUK_OKUMA_SAPKALARI,
  YAPILANDIRMA_OKUMA_SAPKALARI,
  YUKLENICI_OKUMA_SAPKALARI,
  YUKUMLULUK_OKUMA_SAPKALARI,
  PORTAL_SAPKALARI,
} from '@isg/validation';
import { MENU_ETIKET, MENU_GRUP_ETIKET } from '../i18n/tr';

/**
 * MENÜ MODELİ (F4·S5·B1 — Kapsam §20.3a; IA T1-T7 · ✏️ sol menü gruplama
 * 29.09.2026 — T4 ve T6 REVİZE).
 *
 * > **Menü bir modül envanteri değil, bir YER TARİFİDİR** — ve tarif ancak
 * > yeni modülün nereye gireceği önceden belliyse ayakta kalır.
 *
 * Üç kural, üçü de kolay delinir:
 *
 * - **T4 (✏️ revize 29.09.2026) — BEŞ GRUP, AÇILIR-KAPANIR.** *21.08.2026'da
 *   "üç başlıklı grup, açılır-kapanır DEĞİL" denmişti (21 satır katlamayı
 *   gerektirmiyordu).* Grup sayısı beşe çıktı (ürün kararı); grup başlığı
 *   artık bir DÜĞMEDİR. Açık/kapalı durumu bu dosyada DEĞİL `menu-durumu.ts`te
 *   yaşar (tarayıcı deposu; sunucuda tercih tablosu YOK). Model yalnız
 *   *"bu adres hangi grubun"* sorusunu cevaplar (`aktifGrupAnahtari`).
 * - **T5 — şapkası olmayan grup TAMAMEN GİZLENİR**, görünür satırı kalmayan
 *   grubun BAŞLIĞI da çizilmez. Soluk/kilitli satır deseni REDDEDİLDİ:
 *   çalışan şapkasında menü *"kullanılamayan satırlar listesi"*ne dönerdi.
 * - **T6 (✏️ revize 29.09.2026) — HER GRUBUN İÇİ ELLE SIRALI; ALFABETİK GRUP
 *   YOK.** *21.08.2026'da Operasyon grubu alfabetikti (TR harmanlama).* Sıra
 *   artık ürün kararıdır ve aşağıdaki dizinin sırasının KENDİSİDİR — etiket
 *   değişince satır yer değiştirmez. Yeni satır eklemek, yerini de söylemektir.
 *
 * ⚠️ Görünürlük bir YETKİ KARARI DEĞİLDİR (§2.16): karar her istekte
 * guard'dadır, menü yalnız 403 alacak satırı baştan göstermez.
 */

export interface MenuSatiri {
  /** Kararlı anahtar — **e2e ve testid buna bağlanır, ETİKETE DEĞİL.** */
  readonly key: string;
  readonly path: string;
  /** `null` = şapka gerektirmez (bahçe filtresi zaten sunucuda). */
  readonly roller: readonly RoleType[] | null;
}

export type MenuGrupAnahtari = 'kaynaklar' | 'planlama' | 'performans' | 'dokuman' | 'yonetim';

export interface MenuGrubu {
  readonly key: MenuGrupAnahtari;
  /** ⚠️ SIRA DİZİNİN SIRASIDIR (T6 ✏️) — sıralayan bir kod yolu yoktur. */
  readonly satirlar: readonly MenuSatiri[];
}

// ── Şapka kümeleri ─────────────────────────────────────────────────────────
//
// ✏️ **TUR 7 / M2 — MENÜ ARTIK ROL LİSTESİ YAZMAZ, İMPORT EDER.**
//
// Buradaki listeler ELLE yazılıyordu ve sunucunun `@RequireRole`ünden **beş
// kez** ayrıştı. Biri CANLI ARIZAYDI (*Sözlükler* satırı lokasyon yön. ve
// teknisyene çiziliyor, ekran 403 veriyordu — §2.40'ın en kötü kolu); diğer
// dördü ters yönde sessizdi: uç izin veriyor, **menüde satır yok** (tur 6'nın
// evrak onaycısı `location_manager` kendi modülünü menüde göremiyordu).
//
// Kaynak `@isg/validation modul-rolleri.ts`tir ve iki taraf da onu okur.
// Menü SUNUCUDAN BESLENMEZ — görünürlük bir yetki kararı değildir (§2.16);
// paylaşılan olan **liste**dir, kararın kendisi değil.
//
// ⚠️ **MENÜ YALNIZ DARALTABİLİR, GENİŞLETEMEZ** (`menu.spec.ts` ⊆ ile
// kilitler). Bugün tek daraltma `contractor_rep`tir ve mekaniktir: temsilci
// ana kabuğa hiç girmez, `PortalShellLayout` açar (§18.2).

/** Tek DARALTMA: portal ayrı kabuk — temsilci bu menüyü hiç görmez. */
function icSapkalar(liste: readonly RoleType[]): readonly RoleType[] {
  return liste.filter((rol) => !PORTAL_SAPKALARI.includes(rol));
}

const ORG_ROLLERI = icSapkalar(ORGANIZASYON_OKUMA_SAPKALARI);
const CALISAN_ROLLERI = icSapkalar(CALISAN_OKUMA_SAPKALARI);
const YUKLENICI_ROLLERI = icSapkalar(YUKLENICI_OKUMA_SAPKALARI);
const EKIPMAN_ROLLERI = icSapkalar(EKIPMAN_OKUMA_SAPKALARI);
const AKSIYON_ROLLERI = icSapkalar(AKSIYON_OKUMA_SAPKALARI);
const FORM_ROLLERI = icSapkalar(FORM_OKUMA_SAPKALARI);
const EGITIM_ROLLERI = icSapkalar(EGITIM_OKUMA_SAPKALARI);
const RISK_ROLLERI = icSapkalar(RISK_OKUMA_SAPKALARI);
const UYGUNSUZLUK_ROLLERI = icSapkalar(UYGUNSUZLUK_OKUMA_SAPKALARI);
const YUKUMLULUK_ROLLERI = icSapkalar(YUKUMLULUK_OKUMA_SAPKALARI);
const SOZLUK_ROLLERI = icSapkalar(SOZLUK_OKUMA_SAPKALARI);
const KULLANICI_ROLLERI = icSapkalar(KULLANICI_OKUMA_SAPKALARI);
const YAPILANDIRMA_ROLLERI = icSapkalar(YAPILANDIRMA_OKUMA_SAPKALARI);
/** §16.11/10 — izleyici ve saha sorumlusu YOK; uç da 403 verir. */
const SAGLIK_ROLLERI = icSapkalar(SAGLIK_OKUMA_SAPKALARI);
/** Saha turu / denetim (Kapsam §27) — ana okuma ucu `GET site-tours`. */
const SAHA_TURU_ROLLERI = icSapkalar(SAHA_TURU_OKUMA_SAPKALARI);
/** İSG kurulu (Kapsam §28) — ana okuma ucu `GET committees`. */
const KURUL_ROLLERI = icSapkalar(KURUL_OKUMA_SAPKALARI);
/** Acil durum (Kapsam §29) — ana okuma ucu `GET emergency`. */
const ACIL_DURUM_ROLLERI = icSapkalar(ACIL_DURUM_OKUMA_SAPKALARI);
/** Doküman yönetimi (Kapsam §31) — ana okuma ucu `GET documents`. */
const DOKUMAN_ROLLERI = icSapkalar(DOKUMAN_OKUMA_SAPKALARI);

/**
 * MENÜ SATIRI → O MODÜLÜN ANA OKUMA UCUNUN SABİTİ (M3'ün ölçtüğü harita).
 *
 * `null` = satır şapka gerektirmez (İş izinleri · Olaylar · Uygunsuzluklar ·
 * Genel bakış); orada ölçülecek bir daraltma yoktur.
 */
export const MENU_OKUMA_KAYNAGI: Readonly<Record<string, readonly RoleType[]>> = {
  organizasyon: ORGANIZASYON_OKUMA_SAPKALARI,
  calisanlar: CALISAN_OKUMA_SAPKALARI,
  yukleniciler: YUKLENICI_OKUMA_SAPKALARI,
  ekipman: EKIPMAN_OKUMA_SAPKALARI,
  aksiyonlar: AKSIYON_OKUMA_SAPKALARI,
  egitimler: EGITIM_OKUMA_SAPKALARI,
  formlar: FORM_OKUMA_SAPKALARI,
  risk: RISK_OKUMA_SAPKALARI,
  saglik: SAGLIK_OKUMA_SAPKALARI,
  'yasal-uygunluk': YUKUMLULUK_OKUMA_SAPKALARI,
  uygunsuzluklar: UYGUNSUZLUK_OKUMA_SAPKALARI,
  'saha-turlari': SAHA_TURU_OKUMA_SAPKALARI,
  'isg-kurulu': KURUL_OKUMA_SAPKALARI,
  'acil-durum': ACIL_DURUM_OKUMA_SAPKALARI,
  dokumanlar: DOKUMAN_OKUMA_SAPKALARI,
  sozlukler: SOZLUK_OKUMA_SAPKALARI,
  kullanicilar: KULLANICI_OKUMA_SAPKALARI,
  yapilandirma: YAPILANDIRMA_OKUMA_SAPKALARI,
};

/**
 * ONAYLANAN MENÜ — **beş grup, 20 satır** (+ grupsuz *Genel bakış*).
 *
 * ✏️ **29.09.2026 (ürün sahibi — sol menü gruplama):** üç grup (Kuruluş ·
 * Operasyon · Sistem) beş gruba ayrıldı; grup ve satır sırası ürün kararıdır
 * (Kapsam §20.3a — onaylanan menü). **Satır etiketleri ve adresleri
 * DEĞİŞMEDİ** — yalnız yerleşim.
 *
 * ⚠️ **SATIRIN ŞAPKA LİSTESİ DE DEĞİŞMEDİ:** satır grup değiştirdi, `roller`i
 * aynen taşıdı (envanter `menu-read-uc-envanteri.spec`).
 */
export const MENU_GRUPLARI: readonly MenuGrubu[] = [
  {
    key: 'kaynaklar', // yapı → insan → dış taraf → varlık
    satirlar: [
      { key: 'organizasyon', path: '/organizasyon', roller: ORG_ROLLERI },
      { key: 'calisanlar', path: '/calisanlar', roller: CALISAN_ROLLERI },
      { key: 'yukleniciler', path: '/yukleniciler', roller: YUKLENICI_ROLLERI },
      { key: 'ekipman', path: '/ekipman', roller: EKIPMAN_ROLLERI },
    ],
  },
  {
    key: 'planlama',
    satirlar: [
      // İş izinleri + Olaylar HERKESTE: talep açmak / olay bildirmek şapka
      // gerektirmez (§20.5); liste zaten bahçe filtrelidir.
      { key: 'izinler', path: '/izinler', roller: null },
      { key: 'saha-turlari', path: '/saha-turlari', roller: SAHA_TURU_ROLLERI },
      { key: 'risk', path: '/risk', roller: RISK_ROLLERI },
      { key: 'egitimler', path: '/egitimler', roller: EGITIM_ROLLERI },
      { key: 'saglik', path: '/saglik', roller: SAGLIK_ROLLERI },
      { key: 'acil-durum', path: '/acil-durum', roller: ACIL_DURUM_ROLLERI },
      { key: 'isg-kurulu', path: '/isg-kurulu', roller: KURUL_ROLLERI },
    ],
  },
  {
    key: 'performans',
    satirlar: [
      { key: 'olaylar', path: '/olaylar', roller: null },
      // ⚠️ §24/3 "tüm iç çalışanlar" — TUR 7 / M2: `roller: null` yerine ucun
      // KENDİ sabiti (iç şapkalar + izleyici). Aynı küme, uçla birlikte değişir.
      { key: 'uygunsuzluklar', path: '/uygunsuzluklar', roller: UYGUNSUZLUK_ROLLERI },
      { key: 'aksiyonlar', path: '/aksiyonlar', roller: AKSIYON_ROLLERI },
      { key: 'yasal-uygunluk', path: '/yasal-uygunluk', roller: YUKUMLULUK_ROLLERI },
    ],
  },
  {
    key: 'dokuman',
    satirlar: [
      // ✏️ 17.09.2026'da (D2-2) *Dokümanlar* Kuruluş grubuna taşınmıştı;
      // 29.09.2026'da kendi grubuna çıktı ve *Formlar* yanına geldi.
      // ⚠️ K4 AYNEN: *Dokümanlar* (raf) ile *Formlar* (motor) ayrı satırlardır.
      { key: 'dokumanlar', path: '/dokumanlar', roller: DOKUMAN_ROLLERI },
      { key: 'formlar', path: '/formlar', roller: FORM_ROLLERI },
    ],
  },
  {
    key: 'yonetim', // kurulum sırası
    satirlar: [
      { key: 'yapilandirma', path: '/yapilandirma', roller: YAPILANDIRMA_ROLLERI },
      { key: 'sozlukler', path: '/sozluk-bakim', roller: SOZLUK_ROLLERI },
      { key: 'kullanicilar', path: '/kullanicilar', roller: KULLANICI_ROLLERI },
    ],
  },
];

/** Grupsuz, en üstte duran tek satır (IA §4). */
export const GENEL_BAKIS: MenuSatiri = { key: 'genel-bakis', path: '/', roller: null };

export interface CizilecekGrup {
  readonly key: MenuGrubu['key'];
  readonly baslik: string;
  readonly satirlar: readonly { key: string; path: string; label: string }[];
}

/**
 * Şapka kümesinden çizilecek grupları üretir (T5).
 * **Görünür satırı kalmayan grup HİÇ DÖNMEZ** — başlığı da çizilmesin diye.
 * Satır sırası `MENU_GRUPLARI`nın sırasıdır (T6 ✏️ — sıralama YOK).
 */
export function menuGruplari(sapkalar: readonly RoleType[]): CizilecekGrup[] {
  const gorunur = (satir: MenuSatiri): boolean =>
    satir.roller === null || satir.roller.some((rol) => sapkalar.includes(rol));

  return MENU_GRUPLARI.map((grup) => {
    const satirlar = grup.satirlar.filter(gorunur).map((satir) => ({
      key: satir.key,
      path: satir.path,
      label: MENU_ETIKET[satir.key] ?? satir.key,
    }));
    return { key: grup.key, baslik: MENU_GRUP_ETIKET[grup.key] ?? grup.key, satirlar };
  }).filter((grup) => grup.satirlar.length > 0);
}

/**
 * ADRESİN GRUBU — *"aktif sayfanın grubu açık başlar"* kuralının yüklemi.
 *
 * Eşleşme **bölüt sınırındadır** (`/risk` → `/risk/yeni` evet, `/riskli` hayır)
 * ve react-router `NavLink`in aktif satır kuralının aynısıdır: grubu açılan
 * adres ile satırı boyanan adres AYRIŞMAZ. Menüde satırı olmayan adres
 * (*Genel bakış* · `/islerim` · eski takma adlar) `null` döner — açılacak grup
 * yoktur.
 *
 * ⚠️ Şapkaya BAKMAZ: adresin grubu bir yer bilgisidir. Grubu göremeyen
 * kullanıcıda o grup zaten çizilmez (`menuGruplari`).
 */
export function aktifGrupAnahtari(adres: string): MenuGrupAnahtari | null {
  const grup = MENU_GRUPLARI.find((aday) =>
    aday.satirlar.some((satir) => adres === satir.path || adres.startsWith(`${satir.path}/`)),
  );
  return grup?.key ?? null;
}

/**
 * TEK MENÜ SATIRININ GÖRÜNÜRLÜĞÜ — `menuGruplari`nın AYNI kuralı (dilim 2).
 * Bir ekran başka bir modüle BAĞ çizecekse bağ yalnız o satırı görebilene
 * çizilir: görünür bağ + 403 sayfa §2.40'ın en kötü koludur. Kural burada,
 * ekranda şapka dizgesi yazılmaz.
 */
export function menuSatiriGorunur(sapkalar: readonly RoleType[], key: string): boolean {
  return menuGruplari(sapkalar).some((grup) => grup.satirlar.some((satir) => satir.key === key));
}

/**
 * MODÜL İÇİ YAPILANDIRMA SEKMESİ GÖRÜNÜRLÜĞÜ — **SUNUCUDAN** (IA §3/4).
 * Ekran ikinci bir şapka listesi tutmaz; alan `my-authorization`dan gelir ve
 * kaynağı ilgili ucun `@RequireRole` listesidir (görünmeyen sekmenin ucu 403).
 */
export function yuzeyGorunur(
  authz: MyAuthorization,
  anahtar: keyof MyAuthorization['yuzeyler'],
): boolean {
  return authz.yuzeyler[anahtar];
}
